import * as React from "react"
import {
  Toast,
  ToastActionElement,
  ToastProps,
} from "@/components/ui/toast"

const TOAST_LIMIT = 20
const TOAST_REMOVE_DELAY = 1000000

type ToasterToast = ToastProps & {
  id: string
  title?: React.ReactNode
  description?: React.ReactNode
  action?: ToastActionElement
}

const actionTypes = {
  ADD_TOAST: "ADD_TOAST",
  UPDATE_TOAST: "UPDATE_TOAST",
  DISMISS_TOAST: "DISMISS_TOAST",
  REMOVE_TOAST: "REMOVE_TOAST",
} as const

type ActionType = typeof actionTypes

type Action =
  | {
      type: ActionType["ADD_TOAST"]
      toast: ToasterToast
    }
  | {
      type: ActionType["UPDATE_TOAST"]
      toast: Partial<ToasterToast>
    }
  | {
      type: ActionType["DISMISS_TOAST"]
      toastId?: string
    }
  | {
      type: ActionType["REMOVE_TOAST"]
      toastId?: string
    }

interface State {
  toasts: ToasterToast[]
}

const toastTimeouts = new Map<string, ReturnType<typeof setTimeout>>()

const clearToastTimeout = (toastId: string) => {
  if (toastTimeouts.has(toastId)) {
    clearTimeout(toastTimeouts.get(toastId))
    toastTimeouts.delete(toastId)
  }
}

const validateToast = (toast: ToasterToast): ToasterToast => {
  if (!toast.id) {
    console.warn("Toast must have an id")
    toast.id = genId()
  }
  
  return toast
}

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case "ADD_TOAST": {
      if (state.toasts.length >= TOAST_LIMIT) {
        console.warn(`Toast limit of ${TOAST_LIMIT} reached. Discarding oldest toast.`)
        const [, ...remainingToasts] = state.toasts
        return {
          ...state,
          toasts: [...remainingToasts, validateToast(action.toast)],
        }
      }

      return {
        ...state,
        toasts: [...state.toasts, validateToast(action.toast)],
      }
    }

    case "UPDATE_TOAST": {
      if (!action.toast.id) {
        console.error("Cannot update toast: missing id")
        return state
      }

      return {
        ...state,
        toasts: state.toasts.map((t) =>
          t.id === action.toast.id ? { ...t, ...action.toast } : t
        ),
      }
    }

    case "DISMISS_TOAST": {
      const { toastId } = action

      if (toastId === undefined) {
        return {
          ...state,
          toasts: state.toasts.map((t) => ({
            ...t,
            open: false,
          })),
        }
      }

      const toastToUpdate = state.toasts.find(t => t.id === toastId)
      if (!toastToUpdate) {
        console.warn(`Toast with id ${toastId} not found for dismissal`)
        return state
      }

      return {
        ...state,
        toasts: state.toasts.map((t) =>
          t.id === toastId ? { ...t, open: false } : t
        ),
      }
    }

    case "REMOVE_TOAST": {
      const { toastId } = action

      if (toastId === undefined) {
        state.toasts.forEach(t => {
          if (t.id) clearToastTimeout(t.id)
        })
        
        return {
          ...state,
          toasts: [],
        }
      }

      if (toastId) {
        clearToastTimeout(toastId)
      }

      const toastToRemove = state.toasts.find(t => t.id === toastId)
      if (!toastToRemove && toastId) {
        console.warn(`Toast with id ${toastId} not found for removal`)
        return state
      }

      return {
        ...state,
        toasts: state.toasts.filter((t) => t.id !== toastId),
      }
    }

    default: {
      console.error(`Unhandled action type: ${(action as any).type}`)
      return state
    }
  }
}

let count = 0

function genId() {
  count = (count + 1) % Number.MAX_SAFE_INTEGER
  return count.toString()
}

const listeners: Array<(state: State) => void> = []

let memoryState: State = { toasts: [] }

function dispatch(action: Action) {
  try {
    memoryState = reducer(memoryState, action)
    listeners.forEach((listener) => {
      listener(memoryState)
    })
  } catch (error) {
    console.error("Error in toast dispatch:", error)
  }
}

export type ToastOptions = Omit<ToasterToast, "id">

type Toast = Omit<ToasterToast, "id">

function toast(props: Toast) {
  try {
    const id = genId()

    const update = (updatedProps: Toast) => {
      try {
        dispatch({
          type: "UPDATE_TOAST",
          toast: { ...updatedProps, id },
        })
        return {
          id,
          dismiss,
          update
        }
      } catch (error) {
        console.error("Error updating toast:", error)
        return { id, dismiss, update }
      }
    }

    const dismiss = () => {
      try {
        dispatch({ type: "DISMISS_TOAST", toastId: id })
      } catch (error) {
        console.error("Error dismissing toast:", error)
      }
    }

    dispatch({
      type: "ADD_TOAST",
      toast: {
        ...props,
        id,
        open: true,
        onOpenChange: (open) => {
          if (!open) {
            dismiss()
          }
          props.onOpenChange?.(open)
        },
      },
    })

    return {
      id,
      dismiss,
      update,
    }
  } catch (error) {
    console.error("Error creating toast:", error)
    const id = genId()
    return {
      id,
      dismiss: () => {},
      update: () => ({ id, dismiss: () => {}, update: () => ({}) }),
    }
  }
}

function useToast() {
  const [state, setState] = React.useState<State>(memoryState)

  React.useEffect(() => {
    try {
      listeners.push(setState)
      
      return () => {
        const index = listeners.indexOf(setState)
        if (index > -1) {
          listeners.splice(index, 1)
        }
      }
    } catch (error) {
      console.error("Error in useToast hook:", error)
    }
  }, [state])

  return {
    ...state,
    toast,
    dismiss: (toastId?: string) => {
      try {
        dispatch({ type: "DISMISS_TOAST", toastId })
      } catch (error) {
        console.error("Error in dismiss toast:", error)
      }
    },
  }
}

export { useToast, toast }
